import { getStorageOwner } from '../privacy/ownerStorage';
/**
 * IndexedDB Event Storage
 *
 * Provides offline-first event storage with quota management.
 * Critical events are never pruned even under quota pressure.
 */

import type { CanonicalEvent, SyncStatus } from './types';
import { isCriticalEvent } from './types';

// ============================================
// CONSTANTS
// ============================================

function databaseName() { const owner=getStorageOwner(); return `rf-events-v2:${encodeURIComponent(owner.tenantId)}:${encodeURIComponent(owner.userId)}`; }
let currentName = '';
const DB_VERSION = 1;
const EVENTS_STORE = 'events';
const DEAD_LETTER_STORE = 'dead_letter';

// Storage limits
const _QUOTA_WARNING_THRESHOLD_MB = 40;  // Warn at 40MB (reserved for future use)
void _QUOTA_WARNING_THRESHOLD_MB;
const QUOTA_CRITICAL_THRESHOLD_MB = 50; // Start pruning at 50MB
const MAX_EVENTS_TO_KEEP = 10000;       // Maximum events in queue
const PRUNE_BATCH_SIZE = 100;           // Events to prune per batch

// ============================================
// DATABASE INITIALIZATION
// ============================================

let dbInstance: IDBDatabase | null = null;

/**
 * Open or create the IndexedDB database
 */
export async function openDatabase(): Promise<IDBDatabase> {
  const name=databaseName();
  if (dbInstance && currentName===name) {
    return dbInstance;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name, DB_VERSION);

    request.onerror = () => {
      reject(new Error(`Failed to open database: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      if(databaseName()!==name) { request.result.close(); reject(new Error('Session changed')); return; }
      if(dbInstance) dbInstance.close();
      currentName=name; dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Events store with indexes for efficient querying
      if (!db.objectStoreNames.contains(EVENTS_STORE)) {
        const eventsStore = db.createObjectStore(EVENTS_STORE, {
          keyPath: 'idempotency_key',
        });

        // Index for sync status (find pending events)
        eventsStore.createIndex('sync_status', '_local.sync_status', { unique: false });

        // Index for event name (analytics)
        eventsStore.createIndex('event_name', 'event_name', { unique: false });

        // Index for occurred_at (ordering and pruning)
        eventsStore.createIndex('occurred_at', 'occurred_at', { unique: false });

        // Index for priority-based pruning
        eventsStore.createIndex('priority', '_local.priority', { unique: false });
      }

      // Dead letter store for failed events
      if (!db.objectStoreNames.contains(DEAD_LETTER_STORE)) {
        const deadLetterStore = db.createObjectStore(DEAD_LETTER_STORE, {
          keyPath: 'idempotency_key',
        });

        deadLetterStore.createIndex('failed_at', '_local.failed_at', { unique: false });
      }
    };
  });
}

/**
 * Close the database connection
 */
export function closeDatabase(): void {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}

// ============================================
// EVENT STORAGE OPERATIONS
// ============================================

/**
 * Store an event in IndexedDB
 */
export async function storeEvent(event: CanonicalEvent): Promise<void> {
  const db = await openDatabase();

  // Add local metadata
  const eventWithMeta: CanonicalEvent = {
    ...event,
    _local: {
      sync_status: 'pending',
      sync_attempts: 0,
      ...event._local,
    },
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE], 'readwrite');
    const store = transaction.objectStore(EVENTS_STORE);
    const request = store.put(eventWithMeta);

    request.onerror = () => {
      reject(new Error(`Failed to store event: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      resolve();
    };
  });
}

/**
 * Get all pending events for sync
 */
export async function getPendingEvents(limit = 50): Promise<CanonicalEvent[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE], 'readonly');
    const store = transaction.objectStore(EVENTS_STORE);
    const index = store.index('sync_status');
    const request = index.getAll(IDBKeyRange.only('pending'), limit);

    request.onerror = () => {
      reject(new Error(`Failed to get pending events: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      resolve(request.result || []);
    };
  });
}

/**
 * Update event sync status
 */
export async function updateEventStatus(
  idempotencyKey: string,
  status: SyncStatus,
  error?: string
): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE], 'readwrite');
    const store = transaction.objectStore(EVENTS_STORE);
    const getRequest = store.get(idempotencyKey);

    getRequest.onerror = () => {
      reject(new Error(`Failed to get event: ${getRequest.error?.message}`));
    };

    getRequest.onsuccess = () => {
      const event = getRequest.result as CanonicalEvent | undefined;
      if (!event) {
        resolve(); // Event not found, nothing to update
        return;
      }

      const updatedEvent: CanonicalEvent = {
        ...event,
        _local: {
          ...event._local,
          sync_status: status,
          sync_attempts: (event._local?.sync_attempts || 0) + 1,
          last_sync_attempt: new Date().toISOString(),
          last_sync_error: error,
        },
      };

      const putRequest = store.put(updatedEvent);

      putRequest.onerror = () => {
        reject(new Error(`Failed to update event: ${putRequest.error?.message}`));
      };

      putRequest.onsuccess = () => {
        resolve();
      };
    };
  });
}

/**
 * Mark events as synced and optionally remove from local storage
 */
export async function markEventsSynced(
  idempotencyKeys: string[],
  removeAfterSync = false
): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE], 'readwrite');
    const store = transaction.objectStore(EVENTS_STORE);

    let completed = 0;

    for (const key of idempotencyKeys) {
      if (removeAfterSync) {
        const deleteRequest = store.delete(key);
        deleteRequest.onsuccess = () => {
          completed++;
          if (completed === idempotencyKeys.length) {
            resolve();
          }
        };
        deleteRequest.onerror = () => {
          reject(new Error(`Failed to delete synced event: ${deleteRequest.error?.message}`));
        };
      } else {
        const getRequest = store.get(key);
        getRequest.onsuccess = () => {
          const event = getRequest.result as CanonicalEvent | undefined;
          if (event) {
            const updated: CanonicalEvent = {
              ...event,
              _local: {
                ...event._local,
                sync_status: 'synced',
                sync_attempts: event._local?.sync_attempts || 0,
              },
            };
            store.put(updated);
          }
          completed++;
          if (completed === idempotencyKeys.length) {
            resolve();
          }
        };
      }
    }

    if (idempotencyKeys.length === 0) {
      resolve();
    }
  });
}

/**
 * Move event to dead letter queue
 */
export async function moveToDeadLetter(
  event: CanonicalEvent,
  reason: string
): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE, DEAD_LETTER_STORE], 'readwrite');
    const eventsStore = transaction.objectStore(EVENTS_STORE);
    const deadLetterStore = transaction.objectStore(DEAD_LETTER_STORE);

    // Add to dead letter with failure info
    const deadLetterEvent = {
      ...event,
      _local: {
        ...event._local,
        sync_status: 'dead_letter' as SyncStatus,
        failed_at: new Date().toISOString(),
        failure_reason: reason,
      },
    };

    const addRequest = deadLetterStore.put(deadLetterEvent);
    addRequest.onerror = () => {
      reject(new Error(`Failed to add to dead letter: ${addRequest.error?.message}`));
    };

    addRequest.onsuccess = () => {
      // Remove from events store
      const deleteRequest = eventsStore.delete(event.idempotency_key);
      deleteRequest.onerror = () => {
        reject(new Error(`Failed to remove from events: ${deleteRequest.error?.message}`));
      };
      deleteRequest.onsuccess = () => {
        resolve();
      };
    };
  });
}

// ============================================
// QUOTA MANAGEMENT
// ============================================

/**
 * Get current storage usage estimate
 */
export async function getStorageUsage(): Promise<{
  usedMB: number;
  quotaMB: number;
  percentUsed: number;
}> {
  if (!navigator.storage?.estimate) {
    // Fallback for browsers without Storage API
    return { usedMB: 0, quotaMB: 50, percentUsed: 0 };
  }

  const estimate = await navigator.storage.estimate();
  const usedMB = (estimate.usage || 0) / (1024 * 1024);
  const quotaMB = (estimate.quota || 50 * 1024 * 1024) / (1024 * 1024);
  const percentUsed = (usedMB / quotaMB) * 100;

  return { usedMB, quotaMB, percentUsed };
}

/**
 * Get event count in storage
 */
export async function getEventCount(): Promise<number> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE], 'readonly');
    const store = transaction.objectStore(EVENTS_STORE);
    const request = store.count();

    request.onerror = () => {
      reject(new Error(`Failed to count events: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };
  });
}

/**
 * Check if quota management is needed
 */
export async function needsQuotaManagement(): Promise<{
  needsPruning: boolean;
  reason?: 'storage_limit' | 'event_count';
  usedMB?: number;
  eventCount?: number;
}> {
  const [usage, count] = await Promise.all([
    getStorageUsage(),
    getEventCount(),
  ]);

  if (usage.usedMB >= QUOTA_CRITICAL_THRESHOLD_MB) {
    return {
      needsPruning: true,
      reason: 'storage_limit',
      usedMB: usage.usedMB,
    };
  }

  if (count >= MAX_EVENTS_TO_KEEP) {
    return {
      needsPruning: true,
      reason: 'event_count',
      eventCount: count,
    };
  }

  return { needsPruning: false };
}

/**
 * Prune old, low-priority events to free space
 * NEVER prunes critical events
 */
export async function pruneEvents(): Promise<number> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE], 'readwrite');
    const store = transaction.objectStore(EVENTS_STORE);
    const index = store.index('occurred_at');

    // Get oldest events first
    const request = index.openCursor();
    let pruned = 0;
    const toPrune: string[] = [];

    request.onerror = () => {
      reject(new Error(`Failed to prune events: ${request.error?.message}`));
    };

    request.onsuccess = (cursorEvent) => {
      const cursor = (cursorEvent.target as IDBRequest<IDBCursorWithValue>).result;

      if (cursor && toPrune.length < PRUNE_BATCH_SIZE) {
        const event = cursor.value as CanonicalEvent;

        // Never prune critical events
        if (!isCriticalEvent(event.event_name)) {
          // Only prune synced or failed events
          const status = event._local?.sync_status;
          if (status === 'synced') {
            toPrune.push(event.idempotency_key);
          }
        }

        cursor.continue();
      } else {
        // Delete collected events
        for (const key of toPrune) {
          store.delete(key);
          pruned++;
        }
        resolve(pruned);
      }
    };
  });
}

/**
 * Run quota management if needed
 */
export async function manageQuota(): Promise<void> {
  const check = await needsQuotaManagement();

  if (check.needsPruning) {
    console.warn(
      `[EventStorage] Quota management triggered: ${check.reason}`,
      check.usedMB ? `(${check.usedMB.toFixed(2)}MB used)` : `(${check.eventCount} events)`
    );

    const pruned = await pruneEvents();
    console.info(`[EventStorage] Pruned ${pruned} events`);
  }
}

// ============================================
// STATISTICS & DEBUGGING
// ============================================

/**
 * Get storage statistics for observability
 */
export async function getStorageStats(): Promise<{
  totalEvents: number;
  pendingEvents: number;
  syncedEvents: number;
  failedEvents: number;
  deadLetterEvents: number;
  usedMB: number;
  quotaMB: number;
}> {
  const db = await openDatabase();
  const usage = await getStorageUsage();

  const countByStatus = async (status: SyncStatus): Promise<number> => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([EVENTS_STORE], 'readonly');
      const store = transaction.objectStore(EVENTS_STORE);
      const index = store.index('sync_status');
      const request = index.count(IDBKeyRange.only(status));

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  };

  const deadLetterCount = async (): Promise<number> => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([DEAD_LETTER_STORE], 'readonly');
      const store = transaction.objectStore(DEAD_LETTER_STORE);
      const request = store.count();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  };

  const [totalEvents, pending, synced, failed, deadLetter] = await Promise.all([
    getEventCount(),
    countByStatus('pending'),
    countByStatus('synced'),
    countByStatus('failed'),
    deadLetterCount(),
  ]);

  return {
    totalEvents,
    pendingEvents: pending,
    syncedEvents: synced,
    failedEvents: failed,
    deadLetterEvents: deadLetter,
    usedMB: usage.usedMB,
    quotaMB: usage.quotaMB,
  };
}

/**
 * Clear all event data (use with caution - for testing/reset only)
 */
export async function clearAllEvents(): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([EVENTS_STORE, DEAD_LETTER_STORE], 'readwrite');

    const eventsStore = transaction.objectStore(EVENTS_STORE);
    const deadLetterStore = transaction.objectStore(DEAD_LETTER_STORE);

    const clearEvents = eventsStore.clear();
    const clearDeadLetter = deadLetterStore.clear();

    clearEvents.onerror = () => reject(clearEvents.error);
    clearDeadLetter.onerror = () => reject(clearDeadLetter.error);

    transaction.oncomplete = () => resolve();
  });
}

